import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Users, FolderOpen, Calendar, ClipboardList, ChevronRight,
    AlertTriangle, RefreshCcw, Loader2, HandCoins, MapPin, User,
} from "lucide-react";

import OperationsShell from "@/components/operations/OperationsShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import PortalHero from "@/components/ui/PortalHero";
import { portalIdentity } from "@/lib/portalIdentity";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * SALES-ROLE-001B · `/sales` — Sales workspace landing page.
 *
 * Bespoke Sales dashboard that reuses the Operations shell chrome
 * (`OperationsShell`), the shared `PageContainer` / `PageHeader`
 * layout primitives and the existing card patterns. It renders
 * ONLY Sales-relevant sections:
 *
 *   • Sales pipeline breakdown (sales_design / technical_design /
 *     permissions counts + deep links into Projects).
 *   • Active customer count (deep link to Customers).
 *   • My open tasks count (deep link to Tasks scoped to me).
 *   • My upcoming visits (next 14 days, Sales-relevant visit types).
 *   • Quick-nav to permitted surfaces: Customers, Projects,
 *     Calendar, Tasks, Profile.
 *
 * When the aggregate endpoint is unavailable the page renders a
 * clear unavailable state with a retry button — never fabricated
 * zeros. Individual sections still surface loading / empty /
 * "no engineer link" states honestly.
 */
function fmtDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            weekday: "short", day: "numeric", month: "short",
            hour: "numeric", minute: "2-digit",
        });
    } catch (_) { return iso; }
}

function KpiCard({ label, value, icon: Icon, hint, to, tone = "neutral", testid, loading }) {
    // DEV-021 · Restrained KPI card. No heavy top stripe. Hierarchy
    // comes from typography + spacing + a compact tinted icon tile.
    // Semantic colour is applied to the icon tile AND value only
    // when the value is meaningfully non-zero; a zero attention/
    // exception KPI presents calmly (neutral tile, dark value).
    const TONES = {
        neutral:  { iconBg: "bg-[#EEF2FF]", iconFg: "text-[#1E3A8A]", value: "text-[#131B2F]" },
        positive: { iconBg: "bg-[#DCFCE7]", iconFg: "text-[#166534]", value: "text-[#166534]" },
        amber:    { iconBg: "bg-[#FEF3C7]", iconFg: "text-[#B45309]", value: "text-[#B45309]" },
        red:      { iconBg: "bg-[#FEE2E2]", iconFg: "text-[#B91C1C]", value: "text-[#B91C1C]" },
    };
    const isMeaningful = value !== 0 && value !== null && value !== undefined;
    const t = (isMeaningful ? TONES[tone] : TONES.neutral) || TONES.neutral;
    const valueCls = t.value;
    const cardBase =
        "rounded-xl border border-[#E5E7EB] bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all";
    const inner = (
        <>
            <div className="flex items-start gap-3">
                {Icon && (
                    <span className={`inline-flex w-10 h-10 items-center justify-center rounded-lg ${t.iconBg} ${t.iconFg} shrink-0`}>
                        <Icon size={18} strokeWidth={1.75} />
                    </span>
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#4B5565] font-semibold">
                        {label}
                    </div>
                    <div
                        className={`mt-1 text-[28px] leading-none font-semibold tabular-nums ${valueCls}`}
                        data-testid={`${testid}-value`}
                    >
                        {loading ? <span className="text-[#9CA3AF] text-xl">…</span> : (value ?? "—")}
                    </div>
                </div>
            </div>
            {hint && (
                <div className="mt-3 text-xs text-[#4B5565]" data-testid={`${testid}-hint`}>
                    {hint}
                </div>
            )}
        </>
    );
    if (!to) {
        return (
            <div data-testid={testid} className={cardBase}>{inner}</div>
        );
    }
    return (
        <Link
            to={to}
            data-testid={testid}
            className={`block ${cardBase} hover:-translate-y-[1px] hover:shadow-md hover:border-[#CBD5E1] focus:outline-none focus:ring-2 focus:ring-[#3E9E2E]/40`}
        >
            {inner}
        </Link>
    );
}

function NavCard({ to, icon: Icon, label, hint, testid }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className="flex items-center justify-between rounded-xl border border-[#E5E7EB] bg-white p-4 hover:bg-[#F5F5F7] transition-colors"
        >
            <div className="flex items-center gap-3">
                {Icon && <Icon size={16} strokeWidth={1.75} className="text-[#131B2F]" />}
                <div>
                    <div className="text-sm font-medium text-[#131B2F]">{label}</div>
                    {hint && <div className="text-xs text-[#4B5565]">{hint}</div>}
                </div>
            </div>
            <ChevronRight size={14} strokeWidth={2} className="text-[#4B5565]" />
        </Link>
    );
}

export default function SalesDashboard() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const data = await api.salesDashboardSummary();
            setSummary(data);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || "Failed to load your Sales workspace");
            setSummary(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const pipeline = summary?.pipeline;
    const openTasks = summary?.my_work?.open_task_count;
    const upcoming = summary?.my_work?.upcoming_visits || [];
    // DEV-019F UAT · Today's actionable work — Visits + owned
    // active Sales Actions due today. Backend already aggregates
    // and filters (completed/cancelled/superseded excluded, only
    // this user's actions). We keep the fallback to `[]` so a stale
    // client build still renders zero rather than crashing.
    const todayItems = summary?.my_work?.today_items || [];
    const todayCount = todayItems.length;
    const engineerLinked = summary?.engineer_linked;
    const userRole = (user?.role || "").toLowerCase();

    return (
        <OperationsShell>
            <PageContainer width="full" testid="sales-dashboard-page">
                <PageHeader
                    eyebrow="SALES PORTAL"
                    title={`Welcome, ${(user?.name || "").split(" ")[0] || "Sales"}`}
                    subtitle="Your Sales pipeline, upcoming visits and assigned work — every number links to the exact records that produced it."
                    icon={HandCoins}
                    testid="sales-dashboard"
                />

                {/* DEV-022 · Shared premium PortalHero replaces the
                    bespoke navy panel — one hero pattern across all
                    role landings. Role-aware eyebrow so a Sales
                    session never reads "Operations". */}
                {(() => {
                    const identity = portalIdentity(user?.role);
                    const groups = todayItems.reduce((acc, it) => {
                        const k = it?.kind === "sales_action"
                            ? "todo"
                            : it?.kind === "appointment" ? "visit" : it?.kind || "other";
                        acc[k] = (acc[k] || 0) + 1;
                        return acc;
                    }, {});
                    const order = [
                        { key: "todo",  singular: "To-do", plural: "To-do" },
                        { key: "visit", singular: "Visit", plural: "Visits" },
                        { key: "task",  singular: "Task",  plural: "Tasks" },
                    ];
                    const pills = order
                        .filter((c) => (groups[c.key] || 0) > 0)
                        .map((c) => ({ ...c, n: groups[c.key] }));
                    const displayCount = (loading || err) ? "—" : todayCount;
                    const sentence = (loading || err) ? (
                        <>Loading your day…</>
                    ) : todayCount === 0 ? (
                        <>No scheduled work today.</>
                    ) : (
                        <>
                            You have{" "}
                            <strong className="text-white font-semibold">
                                {todayCount} scheduled {todayCount === 1 ? "item" : "items"} today.
                            </strong>
                            {pills.length > 0 && (
                                <span className="ml-2 text-white/70">
                                    {pills.map((p, i) => (
                                        <React.Fragment key={p.key}>
                                            <span className="mx-2 text-white/30">·</span>
                                            <span data-testid={`sales-welcome-breakdown-${p.key}`}>
                                                <span className="tabular-nums font-medium text-white">{p.n}</span>{" "}
                                                {p.n === 1 ? p.singular : p.plural}
                                            </span>
                                            {/* Suppress lint on unused i */}
                                            <span aria-hidden hidden data-i={i} />
                                        </React.Fragment>
                                    ))}
                                </span>
                            )}
                        </>
                    );
                    return (
                        <div className="mb-6" data-testid="sales-welcome-banner">
                            <PortalHero
                                testid="sales-hero"
                                eyebrow={`${identity.eyebrow} · TODAY`}
                                title={`Welcome back, ${((user?.name || "").split(" ")[0]) || "Jordan"}`}
                                sentence={
                                    <span data-testid="sales-welcome-breakdown">
                                        {sentence}
                                    </span>
                                }
                                cta={{
                                    to: "/operations/calendar?focus=today",
                                    label: "View today's schedule",
                                    icon: Calendar,
                                }}
                                ring={{
                                    value: (
                                        <span data-testid="sales-welcome-count">{displayCount}</span>
                                    ),
                                    label: todayCount === 1 ? "item today" : "items today",
                                }}
                                tag="Powered by Pearl Energy"
                            />
                            {/* Preserve historical schedule CTA
                                testid for downstream tests. */}
                            <Link
                                to="/operations/calendar?focus=today"
                                data-testid="sales-welcome-schedule"
                                aria-hidden
                                className="sr-only"
                                tabIndex={-1}
                            >
                                View today's schedule
                            </Link>
                        </div>
                    );
                })()}

                {err && (
                    <div
                        data-testid="sales-dashboard-error"
                        className="mb-4 rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] p-4 flex items-start gap-3"
                    >
                        <AlertTriangle size={16} className="text-[#B91C1C] shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <div className="text-sm font-medium text-[#131B2F]">
                                Sales workspace is unavailable right now
                            </div>
                            <div className="mt-1 text-xs text-[#4B5565]">
                                We couldn't load your pipeline and assignments. Metrics aren't shown as zero — reload
                                once the service is available again.
                            </div>
                            <button
                                type="button"
                                onClick={load}
                                data-testid="sales-dashboard-retry"
                                className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                            >
                                <RefreshCcw size={12} /> Try again
                            </button>
                        </div>
                    </div>
                )}

                {!engineerLinked && !err && !loading && (
                    <div
                        data-testid="sales-dashboard-link-hint"
                        className="mb-4 rounded-xl border border-[#FEF3C7] bg-[#FFFBEB] p-4 text-xs text-[#92400E]"
                    >
                        Your Sales account isn't linked to a resource profile yet, so "my open tasks" and
                        "my upcoming visits" cannot be scoped to you. Ask Operations to link your resource in
                        Field Team Management.
                    </div>
                )}

                {/* DEV-019 UAT CORRECTION · KPI cards regrouped into
                    three logical rows so a Sales user sees at a glance
                    what they are managing, what needs attention and
                    what has recently happened. Values / deep-links /
                    metric semantics are unchanged — only card order
                    and one label ("Active Pipeline" → "Sales
                    Pipeline") since the metric is scoped to the
                    Sales-owned stages set, not the logged-in user's
                    owned projects (see backend `pipeline_total_visible`
                    in `routers/sales.py`).

                    ROW 1 — PORTFOLIO (what I manage) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sales-dashboard-kpis">
                    <KpiCard
                        label="Sales Pipeline"
                        value={err ? null : pipeline?.total}
                        icon={FolderOpen}
                        hint={pipeline ? "Active commercial opportunities in your scope" : undefined}
                        // DEV-019/020 UAT correction · Drill-through URL must
                        // return EXACTLY the population represented by the
                        // KPI count (opportunity_outcome=active within Sales
                        // visibility). The `state=active` alias let already-
                        // completed non-active outcomes bleed through and
                        // made the count/list disagree.
                        to={pipeline?.total ? "/operations/projects?outcome=active" : null}
                        testid="sales-kpi-pipeline"
                        loading={loading}
                    />
                    <KpiCard
                        label="Active customers"
                        value={err ? null : summary?.customers?.active_total}
                        icon={Users}
                        to="/operations/customers"
                        testid="sales-kpi-customers"
                        loading={loading}
                    />
                    <KpiCard
                        label="My active opportunities"
                        value={err ? null : (summary?.opportunities?.my_active ?? 0)}
                        icon={HandCoins}
                        to="/operations/projects?outcome=active"
                        testid="sales-kpi-my-active"
                        loading={loading}
                    />
                </div>

                {/* ROW 2 — ACTION / ATTENTION (what needs me now) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3" data-testid="sales-dashboard-attention">
                    <KpiCard
                        label="My open tasks"
                        value={err ? null : (openTasks ?? "—")}
                        icon={ClipboardList}
                        hint={
                            engineerLinked === false
                                ? "Link your resource profile to see"
                                : (openTasks > 0 ? "Open work assigned to you" : undefined)
                        }
                        // DEV-019 UAT correction 4 · Sales KPI drill-
                        // through must land on the canonical Sales
                        // Tasks route (/sales/tasks) — NOT the Ops
                        // workspace — and use the explicit URL filter
                        // combo (scope=pending + assigned_to_me=true)
                        // that returns the SAME canonical population
                        // as the KPI count. Ops users viewing the
                        // Sales dashboard get the Ops route instead.
                        to={engineerLinked && openTasks > 0
                            ? (userRole === "sales"
                                ? "/sales/tasks?scope=pending&assigned_to_me=true"
                                : "/operations/tasks?scope=pending&assigned_to_me=true")
                            : null}
                        testid="sales-kpi-my-tasks"
                        loading={loading}
                    />
                    <KpiCard
                        label="Actions due today"
                        value={err ? null : (summary?.opportunities?.actions_due_today ?? 0)}
                        icon={AlertTriangle}
                        tone={(summary?.opportunities?.actions_due_today || 0) > 0 ? "amber" : "neutral"}
                        to="/operations/projects?outcome=active&action_due=today&focus_section=sales-actions"
                        testid="sales-kpi-actions-due-today"
                        loading={loading}
                    />
                    <KpiCard
                        label="Overdue sales actions"
                        value={err ? null : (summary?.opportunities?.actions_overdue ?? 0)}
                        icon={AlertTriangle}
                        tone={(summary?.opportunities?.actions_overdue || 0) > 0 ? "red" : "neutral"}
                        to="/operations/projects?outcome=active&action_due=overdue&focus_section=sales-actions"
                        testid="sales-kpi-actions-overdue"
                        loading={loading}
                    />
                </div>

                {/* ROW 3 — FORWARD LOOKING / OUTCOMES */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3" data-testid="sales-dashboard-outcomes">
                    <KpiCard
                        label="Upcoming (14d)"
                        value={err ? null : upcoming.length}
                        icon={Calendar}
                        hint={engineerLinked ? "Sales visits, surveys and walkthroughs" : "Link your resource profile to see"}
                        to={engineerLinked && upcoming.length > 0 ? "/operations/calendar" : null}
                        testid="sales-kpi-upcoming"
                        loading={loading}
                    />
                    <KpiCard
                        label="Missing next action"
                        value={err ? null : (summary?.opportunities?.missing_next_action ?? 0)}
                        icon={ClipboardList}
                        to="/operations/projects?outcome=active&action_due=missing&focus_section=sales-actions"
                        testid="sales-kpi-missing-action"
                        loading={loading}
                    />
                    <KpiCard
                        label="Won (30d)"
                        value={err ? null : (summary?.opportunities?.won_recent ?? 0)}
                        icon={HandCoins}
                        tone={(summary?.opportunities?.won_recent || 0) > 0 ? "positive" : "neutral"}
                        to="/operations/projects?outcome=won"
                        testid="sales-kpi-won"
                        loading={loading}
                    />
                    <KpiCard
                        label="Lost (30d)"
                        value={err ? null : (summary?.opportunities?.lost_recent ?? 0)}
                        icon={AlertTriangle}
                        to="/operations/projects?outcome=lost"
                        testid="sales-kpi-lost"
                        loading={loading}
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mt-6">
                    {/* Pipeline breakdown */}
                    <section
                        className="lg:col-span-2 rounded-xl border border-[#E5E7EB] bg-white p-5"
                        data-testid="sales-pipeline-card"
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <FolderOpen size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                            <div className="text-sm font-semibold text-[#131B2F]">Pipeline by stage</div>
                            {pipeline && (
                                <span className="text-[11px] text-[#4B5565] ml-auto">{pipeline.total} active</span>
                            )}
                        </div>
                        {loading ? (
                            <div className="flex items-center gap-2 text-xs text-[#4B5565]" data-testid="sales-pipeline-loading">
                                <Loader2 className="animate-spin" size={14} /> Loading pipeline…
                            </div>
                        ) : err ? (
                            <div className="text-xs text-[#4B5565]" data-testid="sales-pipeline-unavailable">
                                Pipeline breakdown is unavailable — retry above.
                            </div>
                        ) : (pipeline?.total || 0) === 0 ? (
                            <div className="text-xs text-[#4B5565]" data-testid="sales-pipeline-empty">
                                No active opportunities in your scope right now.
                            </div>
                        ) : (
                            <ul className="space-y-2" data-testid="sales-pipeline-list">
                                {/* DEV-019/020 UAT correction · Render the
                                    canonical commercial lifecycle breakdown
                                    (Enquiry → Decision) from `by_sales_stage`.
                                    The legacy `by_stage` used operational
                                    delivery stages (Sales & Design etc.)
                                    which no longer represent Sales pipeline
                                    state. Deep-link uses the canonical
                                    `sales_stage` filter combined with
                                    `outcome=active` so counts + drill-downs
                                    reconcile exactly. */}
                                {(pipeline.by_sales_stage || []).filter((r) => r.count > 0).map((row) => (
                                    <li
                                        key={row.stage}
                                        className="flex items-center gap-3"
                                        data-testid={`sales-pipeline-row-${row.stage}`}
                                    >
                                        <Link
                                            to={`/operations/projects?outcome=active&sales_stage=${encodeURIComponent(row.stage)}`}
                                            className="flex-1 text-sm text-[#131B2F] hover:underline truncate"
                                            data-testid={`sales-pipeline-link-${row.stage}`}
                                        >
                                            {row.stage_label}
                                        </Link>
                                        <div className="w-40 h-1.5 rounded-full bg-[#F0F1F5] overflow-hidden">
                                            <div
                                                className="h-full bg-[#131B2F]"
                                                style={{
                                                    width: `${pipeline.total ? Math.round((row.count / pipeline.total) * 100) : 0}%`,
                                                }}
                                            />
                                        </div>
                                        <span className="w-8 text-right text-xs text-[#4B5565] tabular-nums">
                                            {row.count}
                                        </span>
                                    </li>
                                ))}
                                {(pipeline.unassigned_active || 0) > 0 && (
                                    <li
                                        key="__unassigned"
                                        className="flex items-center gap-3"
                                        data-testid="sales-pipeline-row-unassigned"
                                    >
                                        <Link
                                            to="/operations/projects?outcome=active&sales_stage=unassigned"
                                            className="flex-1 text-sm text-[#131B2F] hover:underline truncate"
                                            data-testid="sales-pipeline-link-unassigned"
                                        >
                                            Unassigned
                                        </Link>
                                        <div className="w-40 h-1.5 rounded-full bg-[#F0F1F5] overflow-hidden">
                                            <div
                                                className="h-full bg-[#131B2F]"
                                                style={{
                                                    width: `${pipeline.total ? Math.round((pipeline.unassigned_active / pipeline.total) * 100) : 0}%`,
                                                }}
                                            />
                                        </div>
                                        <span className="w-8 text-right text-xs text-[#4B5565] tabular-nums">
                                            {pipeline.unassigned_active}
                                        </span>
                                    </li>
                                )}
                            </ul>
                        )}
                    </section>

                {/* SALES-ROLE-001B · Sales quick-nav — "Pipeline"
                    terminology matches the sidebar label. Records
                    themselves stay on the shared authorised
                    ``/operations/projects`` route. */}
                    <div className="space-y-3" data-testid="sales-quick-nav">
                        <NavCard to="/operations/customers" icon={Users}         label="Customers" hint="Your customer list"    testid="sales-nav-customers" />
                        <NavCard to="/operations/projects"  icon={FolderOpen}    label="Sales Pipeline"  hint="Your sales opportunities" testid="sales-nav-projects" />
                        <NavCard to="/operations/calendar"  icon={Calendar}      label="Calendar"  hint="Visits, availability and clashes" testid="sales-nav-calendar" />
                        <NavCard to="/sales/tasks"          icon={ClipboardList} label="Tasks"     hint="Tasks assigned to you"           testid="sales-nav-tasks" />
                        <NavCard to="/profile"              icon={User}          label="Profile"   hint="Your account and preferences"    testid="sales-nav-profile" />
                    </div>
                </div>

                {/* My upcoming visits */}
                <section
                    className="mt-6 rounded-xl border border-[#E5E7EB] bg-white p-5"
                    data-testid="sales-upcoming-card"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Calendar size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                        <div className="text-sm font-semibold text-[#131B2F]">My upcoming visits</div>
                        <span className="text-[11px] text-[#4B5565] ml-auto">Next 14 days</span>
                    </div>
                    {loading ? (
                        <div className="flex items-center gap-2 text-xs text-[#4B5565]" data-testid="sales-upcoming-loading">
                            <Loader2 className="animate-spin" size={14} /> Loading visits…
                        </div>
                    ) : err ? (
                        <div className="text-xs text-[#4B5565]" data-testid="sales-upcoming-unavailable">
                            Upcoming visits are unavailable — retry above.
                        </div>
                    ) : engineerLinked === false ? (
                        <div className="text-xs text-[#4B5565]" data-testid="sales-upcoming-not-linked">
                            Link your resource profile to see visits assigned to you.
                        </div>
                    ) : upcoming.length === 0 ? (
                        <div className="text-xs text-[#4B5565]" data-testid="sales-upcoming-empty">
                            No upcoming visits assigned to you in the next 14 days.
                        </div>
                    ) : (
                        <ul className="divide-y divide-[#EDEFF3]" data-testid="sales-upcoming-list">
                            {upcoming.map((v) => (
                                <li
                                    key={v.id}
                                    className="py-3 flex items-start gap-3"
                                    data-testid={`sales-upcoming-row-${v.id}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-[#131B2F] truncate">
                                            {v.visit_type} · {v.customer_name || "Customer"}
                                        </div>
                                        {v.address && (
                                            <div className="mt-0.5 text-xs text-[#4B5565] flex items-center gap-1 truncate">
                                                <MapPin size={11} /> {v.address}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-xs text-[#4B5565] whitespace-nowrap">
                                        {fmtDate(v.start_at)}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </PageContainer>
        </OperationsShell>
    );
}
